/**
 * The Recorded wall's Layout menu: how many columns and rows, whether the top-left 2×2 is one
 * big window, and a way back to even sizes or to the opening "1 + 5" shape.
 *
 * Purely presentational: every change goes to the host as an edit, and the host re-flows the
 * cameras (big window first) and says if any no longer fit.
 */
import { useState } from 'react';
import { ChevronDownIcon, MinusIcon, PlusIcon, Squares2X2Icon } from '@heroicons/react/24/outline';
import { Tip } from '../common/Tooltip';
import {
  WALL_PLAYBACK_MAX, WALL_PLAYBACK_MAX_COLS, WALL_PLAYBACK_MAX_ROWS, canHaveBigWindow, editWallPlaybackLayout,
  hasBigWindow, windowCountOf, type WallPlaybackLayout,
} from './wallPlaybackLayout';

export interface WallPlaybackLayoutMenuProps {
  layout: WallPlaybackLayout;
  /** Cameras on the wall — so a shrink that would drop some can say so before the click. */
  cameraCount: number;
  onEdit: (edit: Partial<Pick<WallPlaybackLayout, 'rows' | 'cols' | 'big'>>) => void;
  onEvenSizes: () => void;
  onReset: () => void;
}

export default function WallPlaybackLayoutMenu({ layout, cameraCount, onEdit, onEvenSizes, onReset }: WallPlaybackLayoutMenuProps) {
  const [open, setOpen] = useState(false);
  const big = hasBigWindow(layout);
  const uneven = layout.colSizes.some((f) => Math.abs(f - layout.colSizes[0]) > 0.01)
    || layout.rowSizes.some((f) => Math.abs(f - layout.rowSizes[0]) > 0.01);

  /** Why a stepper is off, or what it is about to cost — null when it is a plain yes. */
  const verdict = (edit: Partial<Pick<WallPlaybackLayout, 'rows' | 'cols' | 'big'>>, limit: string) => {
    const next = editWallPlaybackLayout(layout, edit);
    if (!next) return { ok: false, note: limit };
    const drop = cameraCount - windowCountOf(next);
    return { ok: true, note: drop > 0 ? `Takes ${drop} camera${drop > 1 ? 's' : ''} off the wall` : null };
  };
  const capNote = `At most ${WALL_PLAYBACK_MAX} windows play at once`;

  const stepper = (label: string, axis: 'cols' | 'rows', max: number) => {
    const value = layout[axis];
    const less = verdict({ [axis]: value - 1 }, `At least one ${axis === 'cols' ? 'column' : 'row'}`);
    const more = verdict({ [axis]: value + 1 }, value >= max ? `At most ${max} ${axis === 'cols' ? 'columns' : 'rows'}` : capNote);
    const btn = (v: { ok: boolean; note: string | null }, dir: -1 | 1) => (
      <Tip side="bottom" label={v.note || (dir > 0 ? `Add a ${axis === 'cols' ? 'column' : 'row'}` : `Remove a ${axis === 'cols' ? 'column' : 'row'}`)}>
        <button
          onClick={() => onEdit({ [axis]: value + dir })}
          disabled={!v.ok}
          aria-label={dir > 0 ? `Add ${axis === 'cols' ? 'column' : 'row'}` : `Remove ${axis === 'cols' ? 'column' : 'row'}`}
          className="flex h-7 w-7 items-center justify-center rounded-md border border-border bg-background text-foreground transition-colors hover:border-brand hover:text-brand disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-border disabled:hover:text-foreground"
        >
          {dir > 0 ? <PlusIcon className="h-3.5 w-3.5" /> : <MinusIcon className="h-3.5 w-3.5" />}
        </button>
      </Tip>
    );
    return (
      <div className="flex items-center justify-between gap-3">
        <span className="text-[12px] text-foreground">{label}</span>
        <div className="flex items-center gap-1.5">
          {btn(less, -1)}
          <span className="w-5 text-center text-[13px] font-semibold tabular-nums text-foreground">{value}</span>
          {btn(more, 1)}
        </div>
      </div>
    );
  };

  const bigVerdict = canHaveBigWindow(layout.rows, layout.cols)
    ? verdict({ big: !layout.big }, capNote)
    : { ok: false, note: 'Needs at least 2 columns and 2 rows, and more than a 2 × 2 wall' };

  return (
    <div className="relative">
      <Tip side="bottom" label="Columns, rows and window sizes">
        <button
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground sm:text-sm"
        >
          <Squares2X2Icon className="h-4 w-4" />
          <span className="hidden sm:inline">Layout</span>
          <span className="tabular-nums text-foreground">{layout.cols}×{layout.rows}{big ? ' · 1+' + (windowCountOf(layout) - 1) : ''}</span>
          <ChevronDownIcon className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </Tip>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full z-50 mt-1 w-64 space-y-3 rounded-lg border border-border bg-card p-3 shadow-xl">
            {stepper('Columns', 'cols', WALL_PLAYBACK_MAX_COLS)}
            {stepper('Rows', 'rows', WALL_PLAYBACK_MAX_ROWS)}

            <Tip side="bottom" className="block" label={bigVerdict.note || (layout.big ? 'Play the top-left 2 × 2 as four windows' : 'Play the top-left 2 × 2 as one big window')}>
              <label className={`flex items-center justify-between gap-3 ${bigVerdict.ok ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                <span className="text-[12px] text-foreground">Big window</span>
                <input
                  type="checkbox"
                  checked={big}
                  disabled={!bigVerdict.ok}
                  onChange={() => onEdit({ big: !layout.big })}
                  className="h-3.5 w-3.5 rounded border-border accent-brand"
                />
              </label>
            </Tip>

            <p className="border-t border-border pt-2.5 text-[11px] leading-snug text-muted-foreground">
              {windowCountOf(layout)} of {WALL_PLAYBACK_MAX} windows. Drag the lines between windows to resize them; double-click a line to even it out.
            </p>

            <div className="flex items-center gap-2">
              <button
                onClick={onEvenSizes}
                disabled={!uneven}
                className="flex-1 rounded-md border border-border px-2 py-1.5 text-[12px] font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
              >
                Even sizes
              </button>
              <button
                onClick={() => { onReset(); setOpen(false); }}
                className="flex-1 rounded-md border border-border px-2 py-1.5 text-[12px] font-medium text-foreground transition-colors hover:bg-muted"
              >
                Reset to 1 + 5
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
